import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
 ShieldCheck,
 Layout as LayoutIcon,
 Plus,
 AlertCircle,
 CheckCircle,
 Copy,
 ExternalLink,
 Bookmark
} from 'lucide-react';
import { Card, Button, Modal, Input, useUI } from '../../shared/components/ui';
import { apiCall } from '../../utils/api';
import { buildAppUrl } from '../../shared/utils/appUrl';

function WelcomePage({ mode = 'unconfigured' }: { mode?: 'unconfigured' | 'invalid-app' }) {
 const [searchParams] = useSearchParams();
 const { showToast } = useUI();
 const [provisioning, setProvisioning] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [isModalOpen, setIsModalOpen] = useState(false);
 const [newProgramName, setNewProgramName] = useState('');
 const [accessCode, setAccessCode] = useState('');
 const [fieldError, setFieldError] = useState<string | null>(null);
 const [codeError, setCodeError] = useState<string | null>(null);
 const [authorizedEmail, setAuthorizedEmail] = useState<string | null>(null);
 const [provisioningResult, setProvisioningResult] = useState<{ dbId: string } | null>(null);

 // Check if we just returned from a provision login
 React.useEffect(() => {
  const hasToken = sessionStorage.getItem('session_token');
  const isReturningFromProvision = searchParams.get('mode') === 'provision';
  
  if (hasToken && isReturningFromProvision) {
   // Small delay to ensure the UI has settled
   setTimeout(() => {
    setIsModalOpen(true);
    // Clean up the URL
    window.history.replaceState({}, '', '/');
   }, 100);
  }

  if (hasToken) {
    // Peek into the token to show email (assuming it's a JWT or similar, 
    // but for now we'll just check if session is active)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthorizedEmail('Authenticated'); // We could decode the token if needed
  }
 }, [searchParams]);

 const handleStartProvision = () => {
  const token = sessionStorage.getItem('session_token');
  
  if (!token) {
   // Step 1: Must authenticate before we even ask for a name
   window.location.href = `/auth/login?mode=provision`;
   return;
  }

  // Step 2: If authenticated, show the name prompt
  setNewProgramName('');
  setAccessCode('');
  setFieldError(null);
  setCodeError(null);
  setIsModalOpen(true);
 };

 const handleConfirmProvision = async () => {
  let hasError = false;
  if (!newProgramName.trim()) {
   setFieldError('Program name is required');
   hasError = true;
  }
  if (!accessCode.trim()) {
   setCodeError('Access code is required');
   hasError = true;
  }
  if (hasError) return;

  try {
   setProvisioning(true);
   setError(null);
   setFieldError(null);
   setCodeError(null);

   const result = await apiCall<{ dbId: string }>('provisionNewProgram', {
    programName: newProgramName.trim(),
    accessCode: accessCode.trim()
   });
   setProvisioningResult(result);
   setProvisioning(false);
   setIsModalOpen(false);
  } catch (e: any) {
   console.error('Provisioning failed:', e);
   if (e.message?.includes('Unauthorized') || e.message?.includes('Authentication required')) {
    // If unauthorized, redirect to Google Login in provision mode
    window.location.href = `/auth/login?mode=provision`;
    return;
   }
   setError(e.message || 'Failed to provision environment. Please ensure you are signed in to Google.');
   setProvisioning(false);
  }
 };

 return (
  <div className="min-h-screen flex items-center justify-center bg-brand-surface px-4 py-12 sm:px-6 lg:px-8 font-sans">
   <div className="max-w-md w-full">
    <div className="text-center mb-8">
     <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-command rounded-xl shadow-2xl shadow-brand-command/20 mb-8 animate-in zoom-in duration-700">
      <ShieldCheck className="text-white w-10 h-10" />
     </div>
     <h2 className="text-header-xl font-bold text-brand-command tracking-tight uppercase mb-2">
      Gift Request Manager
     </h2>
    </div>

    <Card className="p-10 shadow-2xl border-brand-edge/50 bg-brand-surface/80 backdrop-blur-sm relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute -right-8 -top-8 w-24 h-24 bg-brand-canvas rounded-full blur-2xl opacity-50" />
      <div className="absolute -left-8 -bottom-8 w-24 h-24 bg-brand-canvas rounded-full blur-2xl opacity-50" />

      <div className="relative space-y-8 text-center">
       {error && (
        <div className="bg-danger-surface border border-danger-edge p-4 rounded-xl flex items-start gap-3 text-left animate-in fade-in duration-200">
         <AlertCircle className="text-danger w-5 h-5 shrink-0" />
         <p className="text-badge text-danger font-bold uppercase tracking-tight">{error}</p>
        </div>
       )}

       {mode === 'invalid-app' ? (
       <>
        <div className="mx-auto w-12 h-12 bg-danger-surface rounded-xl flex items-center justify-center mb-4">
         <AlertCircle className="text-danger w-6 h-6" />
        </div>
        <div>
         <h3 className="text-header font-bold text-brand-command uppercase tracking-tight text-danger">Bad URL</h3>
         <p className="text-primary text-brand-label font-bold leading-relaxed">
          This link is invalid or no longer active. Please contact your program administrator for the correct link.
         </p>
        </div>
       </>
      ) : (
        <>
         <div className="mx-auto w-12 h-12 bg-brand-surface rounded-xl flex items-center justify-center mb-4">
          <LayoutIcon className="text-brand-command w-6 h-6" />
         </div>
         <div>
          <h3 className="text-header font-bold text-brand-command uppercase tracking-tight">System Ready</h3>
          <p className="text-primary text-brand-label font-bold leading-relaxed">
           This is the central entry point for the Gift Request Manager. 
           To access a specific portal, please use an authorized launch link.
          </p>
         </div>
        </>
       )}

       <div className="pt-4">
        {authorizedEmail && !provisioning && mode !== 'invalid-app' && (
         <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 bg-brand-surface border border-brand-edge rounded-full animate-in fade-in duration-500">
          <div className="w-1.5 h-1.5 rounded-full bg-brand-command" />
          <span className="text-badge font-bold uppercase text-brand-command tracking-infrastructure">Identity Verified</span>
         </div>
        )}

        {mode !== 'invalid-app' && (
         <>
          <Button 
          variant="primary" 
          className="w-full h-12 text-micro font-bold uppercase tracking-infrastructure shadow-xl shadow-brand-command/10 flex items-center justify-center gap-3"
          onClick={handleStartProvision}
          loading={provisioning}
          >
           {!provisioning && <Plus className="w-4 h-4" />}
           {provisioning ? 'Provisioning Environment...' : authorizedEmail ? 'Continue Setup' : 'Provision New Program'}
          </Button>
          
          {authorizedEmail && !provisioning && (
           <Button 
            variant="ghost"
            className="mt-2 w-full h-10 text-badge font-bold uppercase tracking-infrastructure text-brand-label hover:text-danger"
            onClick={() => {
             sessionStorage.removeItem('session_token');
             window.location.href = '/';
            }}
           >
            Cancel & Sign Out
           </Button>
          )}
          <p className="mt-4 text-badge text-brand-label font-bold uppercase tracking-infrastructure px-4 leading-relaxed opacity-60">
           {authorizedEmail 
           ? 'Your session is authorized. Click continue to name your environment and initialize.' 
           : 'Requires Google Account Authorization. Step 1: Sign in with your Google account.'}
          </p>
         </>
        )}
       </div>
      </div>
    </Card>

    <div className="mt-12 text-center">
     <p className="text-badge text-brand-label/40 font-bold uppercase tracking-infrastructure">
      &copy; {new Date().getFullYear()} RockStop Solutions, LLC.
     </p>
    </div>
   </div>

   {/* Provisioning Modal */}
   <Modal 
    isOpen={isModalOpen} 
    onClose={() => !provisioning && setIsModalOpen(false)}
    title="Provision New Program"
    maxWidth="sm"
   >
    <div className="space-y-6">
     <p className="text-primary text-brand-label font-bold leading-relaxed">
      Enter a descriptive name for this program environment.
     </p>

     <Input
      label="Program Name"
      placeholder="e.g. Regional Program 2026"
      value={newProgramName}
      onChange={(e) => setNewProgramName(e.target.value)}
      error={fieldError || undefined}
      autoFocus
      disabled={provisioning}
     />

     <Input
      label="Access Code"
      placeholder="Provided by RockStop Solutions"
      value={accessCode}
      onChange={(e) => setAccessCode(e.target.value)}
      error={codeError || undefined}
      disabled={provisioning}
     />

     <div className="flex justify-end space-x-3 pt-4 border-t border-brand-edge">
      <Button
       variant="ghost"
       onClick={() => setIsModalOpen(false)}
       disabled={provisioning}
      >
       Cancel
      </Button>
      <Button
       variant="primary"
       onClick={handleConfirmProvision}
       loading={provisioning}
      >
       Initialize Environment
      </Button>
     </div>
    </div>
   </Modal>

   {/* Provisioning Success Modal */}
   <Modal 
    isOpen={!!provisioningResult} 
    onClose={() => setProvisioningResult(null)}
    title="Environment Provisioned"
    maxWidth="lg"
   >
    <div className="space-y-8 py-4">
     <div className="bg-success-surface border border-success-edge rounded-xl p-6 flex items-start space-x-4">
      <CheckCircle className="w-8 h-8 text-success shrink-0" />
      <div>
       <h4 className="text-brand-command font-bold uppercase tracking-tight text-group mb-1">Environment Ready</h4>
       <p className="text-brand-label text-primary font-medium">
        The program environment has been successfully created.
       </p>
      </div>
     </div>

     <div className="grid grid-cols-1 gap-6">
      <div className="space-y-4">
       <label className="block text-micro font-bold uppercase tracking-infrastructure text-brand-label ml-1">Admin Portal Access</label>
       <div className="bg-brand-canvas rounded-xl p-6 border border-brand-edge space-y-4">
        <p className="text-badge text-brand-label font-bold uppercase tracking-tight leading-relaxed">
         Bookmark this unique URL to manage this program in the future.
        </p>
        <div className="flex items-center space-x-2">
         <Input
          value={buildAppUrl(window.location.origin, { app: 'admin', programId: provisioningResult?.dbId })}
          readOnly
          className="bg-brand-surface"
         />
         <Button variant="outline" onClick={() => {
          navigator.clipboard.writeText(buildAppUrl(window.location.origin, { app: 'admin', programId: provisioningResult?.dbId }));
          showToast("URL copied to clipboard", "success");
         }}>
          <Copy size={16} />
         </Button>
        </div>
        <Button
         className="w-full"
         onClick={() => {
          // The session in play here is still the one-hour PROVISIONER session
          // (programId: null) used to call provisionNewProgram. Clear it so landing
          // on the Admin Portal forces a fresh login scoped to the new Program,
          // instead of Entry Point URLs reading a null programId off the stale token.
          sessionStorage.removeItem('session_token');
          window.location.href = buildAppUrl('', { app: 'admin', programId: provisioningResult?.dbId });
         }}
        >
         <ExternalLink size={16} className="mr-2" />
         <span className="text-badge font-bold uppercase tracking-tight">Open Portal Now</span>
        </Button>
       </div>
      </div>
     </div>

     <div className="bg-warning-surface border border-warning-edge rounded-xl p-6 flex items-start space-x-4">
      <Bookmark className="w-8 h-8 text-warning shrink-0" />
      <div>
       <h4 className="text-warning font-bold uppercase tracking-tight text-group">Action Required: Bookmark Page</h4>
       <p className="text-warning text-primary font-medium leading-relaxed">
        This is the only record of this URL — copy or bookmark it now.
       </p>
      </div>
     </div>
    </div>
   </Modal>
  </div>
 );
}

export default WelcomePage;
